import React, { useState } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { useTranslation } from 'react-i18next';


const Services = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Page Header */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-24 md:py-32 pt-[120px]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Our Services</h1>
          <p className="text-xl md:text-2xl text-blue-100">Complete Logistics Solutions for Every Need</p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-blue-600 font-semibold text-sm uppercase tracking-widest mb-3">Services</p>
            <div className="w-24 h-[2px] bg-blue-600 mx-auto mb-8"></div>
            <h2 className="text-4xl font-bold text-gray-900 leading-tight mb-4">
              Comprehensive Logistics Services
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              From shipment booking to inventory management, we provide end-to-end logistics solutions designed for modern businesses.
            </p>
          </div>

          {/* Services Grid */}
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              {
                id: 1,
                icon: '📦',
                title: 'Shipment Booking',
                description: 'Quick and easy shipment booking with instant quotes and confirmation.'
              },
              {
                id: 2,
                icon: '🔍',
                title: 'Real-Time Tracking',
                description: 'Live GPS tracking with location updates at every stage of delivery.'
              },
              {
                id: 3,
                icon: '↩️',
                title: 'Easy Returns',
                description: 'Hassle-free return process for missing or damaged shipments.'
              },
              {
                id: 4,
                icon: '📊',
                title: 'Inventory Management',
                description: 'Centralized tracking and management of stock and inventory.'
              },
              {
                id: 5,
                icon: '📧',
                title: 'Email Notifications',
                description: 'Automated invoices and shipment reports via email.'
              },
              {
                id: 6,
                icon: '👥',
                title: 'User Management',
                description: 'Role-based access control and attendance tracking system.'
              }
            ].map(service => (
              <div key={service.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-300 hover:translate-y-[-2px]">
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Services List */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto divide-y divide-gray-200 bg-gray-50 rounded-xl overflow-hidden">
            {/* Item 1 */}
            <div className="flex gap-8 px-8 py-8 hover:bg-gray-100 transition-colors">
              <div className="text-blue-600 text-4xl font-bold flex-shrink-0 w-12">01</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Shipment Booking & Management</h3>
                <p className="text-gray-600 leading-relaxed">
                  Seamless shipment booking with instant rate calculation, real-time availability, and secure confirmation. Manage multiple shipments from a single dashboard.
                </p>
              </div>
            </div>

            {/* Item 2 */}
            <div className="flex gap-8 px-8 py-8 hover:bg-gray-100 transition-colors">
              <div className="text-blue-600 text-4xl font-bold flex-shrink-0 w-12">02</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Real-Time Shipment Tracking</h3>
                <p className="text-gray-600 leading-relaxed">
                  Track shipments in real-time with GPS coordinates, delivery status updates, and estimated delivery time. Share tracking links with customers.
                </p>
              </div>
            </div>

            {/* Item 3 */}
            <div className="flex gap-8 px-8 py-8 hover:bg-gray-100 transition-colors">
              <div className="text-blue-600 text-4xl font-bold flex-shrink-0 w-12">03</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Returns & Missing Request Management</h3>
                <p className="text-gray-600 leading-relaxed">
                  Handle returns and missing shipment requests efficiently with automated workflows, claim tracking, and resolution management.
                </p>
              </div>
            </div>

            {/* Item 4 */}
            <div className="flex gap-8 px-8 py-8 hover:bg-gray-100 transition-colors">
              <div className="text-blue-600 text-4xl font-bold flex-shrink-0 w-12">04</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Email & WhatsApp Notifications</h3>
                <p className="text-gray-600 leading-relaxed">
                  Automated notifications via email and WhatsApp for shipment updates, invoices, and customer alerts. Customizable notification templates.
                </p>
              </div>
            </div>

            {/* Item 5 */}
            <div className="flex gap-8 px-8 py-8 hover:bg-gray-100 transition-colors">
              <div className="text-blue-600 text-4xl font-bold flex-shrink-0 w-12">05</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Inventory Management System</h3>
                <p className="text-gray-600 leading-relaxed">
                  Centralized inventory tracking with real-time stock updates, SKU management, and usage analytics. Integrated with shipment workflows.
                </p>
              </div>
            </div>

            {/* Item 6 */}
            <div className="flex gap-8 px-8 py-8 hover:bg-gray-100 transition-colors">
              <div className="text-blue-600 text-4xl font-bold flex-shrink-0 w-12">06</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">User Management & Attendance</h3>
                <p className="text-gray-600 leading-relaxed">
                  Role-based access control, employee attendance tracking, and performance analytics. Secure database with encryption.
                </p>
              </div>
            </div>

            {/* Item 7 */}
            <div className="flex gap-8 px-8 py-8 hover:bg-gray-100 transition-colors">
              <div className="text-blue-600 text-4xl font-bold flex-shrink-0 w-12">07</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Automated Billing & Invoicing</h3>
                <p className="text-gray-600 leading-relaxed">
                  Automated invoice generation with detailed cost breakdowns. Monthly billing, payment tracking, and financial reports.
                </p>
              </div>
            </div>

            {/* Item 8 */}
            <div className="flex gap-8 px-8 py-8 hover:bg-gray-100 transition-colors">
              <div className="text-blue-600 text-4xl font-bold flex-shrink-0 w-12">08</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Secure Data Management</h3>
                <p className="text-gray-600 leading-relaxed">
                  Enterprise-grade security with encrypted storage, regular backups, and compliance with data protection standards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Why Choose Us Section */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-blue-600 font-semibold text-sm uppercase tracking-widest mb-3">Why Us</p>
            <div className="w-24 h-[2px] bg-blue-600 mx-auto mb-8"></div>
            <h2 className="text-4xl font-bold text-gray-900">Why Choose Daily Express Cargo</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-blue-600">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Fast & Reliable</h3>
              <p className="text-gray-600">Quick shipment processing with 98% on-time delivery guarantee</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-green-600">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Affordable Pricing</h3>
              <p className="text-gray-600">Competitive rates with transparent billing and no hidden charges</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-purple-600">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Real-Time Tracking</h3>
              <p className="text-gray-600">Live GPS tracking and instant notification updates</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-orange-600">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Customer Support</h3>
              <p className="text-gray-600">24/7 support via chat, phone, email, and WhatsApp</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-red-600">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Safe</h3>
              <p className="text-gray-600">Enterprise-grade encryption and safe handling guaranteed</p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border-t-4 border-pink-600">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Easy Integration</h3>
              <p className="text-gray-600">Simple API integration and quick onboarding process</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
